'use client';

import React from 'react';
import { MultiSelectGroup } from '../MultiSelectGroup';
import { RangeSelectGroup } from '../RangeSelectGroup';
import { SingleSelectGroup } from '../SingleSelectGroup';
import styled from '@benzinga/themetron';

import { Range, Option, MultiRange, MultiRangeLabels } from '../../entities';

type FilterType = 'multi-select' | 'single-select' | 'range-select';
type FilterMethod = 'exact' | 'range';

interface FilterInterface {
  label: string;
  method: FilterMethod;
  key: string;
  type: FilterType;
  selected: any;
}

export interface MultiSelectFilter extends FilterInterface {
  options: Option[];
  selected: Option[];
}

export interface RangeSelectFilter extends FilterInterface {
  selected: Range;
  range: MultiRange;
  rangeLabels?: MultiRangeLabels;
  unit?: string;
  step: number;
}

export interface SingleSelectFilter extends FilterInterface {
  options: Option[];
  selected: Option;
}

export type Filter = MultiSelectFilter | RangeSelectFilter | SingleSelectFilter;

export type FilterLayout = 'vertical' | 'horizontal';

export interface FilterProps {
  filters: Filter[];
  layout?: FilterLayout;
  onChange: (filter: Filter) => void;
  afterFilters?: React.ReactElement;
  beforeFilters?: React.ReactElement;
}

export const Filters: React.FC<React.PropsWithChildren<FilterProps>> = ({
  afterFilters,
  beforeFilters,
  children,
  filters,
  layout = 'vertical',
  onChange,
}) => {
  return (
    <FiltersWrapper layout={layout}>
      {!!beforeFilters && <AfterFiltersWrapper>{beforeFilters}</AfterFiltersWrapper>}
      <FiltersList className="filter-box">
        {filters?.map((filter: Filter) => {
          const { key } = filter;
          switch (filter.type) {
            case 'multi-select': {
              const f = filter as MultiSelectFilter;
              return (
                <MultiSelectGroup
                  key={key}
                  label={f.label}
                  onSelectionChange={(value: Option[]) => {
                    f.selected = value;
                    onChange(f);
                  }}
                  options={f.options}
                  selected={f.selected}
                />
              );
            }
            case 'range-select': {
              const f = filter as RangeSelectFilter;
              return (
                <RangeSelectGroup
                  key={key}
                  label={f.label}
                  onChange={value => {
                    f.selected = value;
                    onChange(f);
                  }}
                  range={f.range}
                  rangeLabels={f.rangeLabels}
                  selected={f.selected}
                  step={f.step}
                  unit={f.unit}
                />
              );
            }
            case 'single-select': {
              const f = filter as SingleSelectFilter;
              return (
                <SingleSelectGroup
                  key={key}
                  label={f.label}
                  onChange={(selected: Option) => {
                    f.selected = selected;
                    onChange(f);
                  }}
                  options={f.options}
                  selected={f.selected}
                />
              );
            }
            default:
              return null;
          }
        })}
        {children}
      </FiltersList>
      {!!afterFilters && <AfterFiltersWrapper>{afterFilters}</AfterFiltersWrapper>}
    </FiltersWrapper>
  );
};

export const FiltersWrapper = styled.div<{ layout: string }>`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background};
  padding: 10px;

  .filter-box {
    flex-direction: ${props => (props.layout === 'vertical' ? 'column' : 'row')};

    @media (max-width: 900px) {
      flex-wrap: wrap;
    }
  }
`;

export const FiltersList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

export const AfterFiltersWrapper = styled.div`
  margin-top: 10px;
`;
